import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import CustomButton from "../common/CustomButton";
import DialogConformationMsg from "./DialogConformationMsg";
import { createLiveLoadAutocompleteColumnFilter } from "./common/createLiveLoadAutocompleteColumn";
import BaseMainFilterAutocomplete from "./BaseMainFilterAutocomplete";
import { mock_divisions } from "../../constData/MOCK_DATA_DRMT";
import { getDrmZipReportData } from "../../redux/reducers/drmCEAZipDownloadSlice";
import { getDrmReportDetails, downloadDrmReportDetails, resetDrmReportDetails } from "../../redux/reducers/drmReportDetailsSlice";
import { downloadZipFiles, exportToExcel } from "../../helpers";
import CustomSnackbar from "../common/CustomSnackbar";

import "./DrmReportFilters.sass";


const BID_REPORTS = [
  {
    key: "BID_TO_CRNT_RGN",
    label: "Bid to Current - Region",
    code: "BID_TO_CRNT_RGN",
    category: "BID_RPT",
    fileName: "Bid_To_Current_Region",
  },
  {
    key: "BID_TO_CRNT_LN",
    label: "Bid to Current - Lane",
    code: "BID_TO_CRNT_LN",
    category: "BID_RPT",
    fileName: "Bid_To_Current_Lane",
  },
  {
    key: "BID_TO_CRNT_SCAC",
    label: "Bid to Current - SCAC",
    code: "BID_TO_CRNT_SCAC",
    category: "BID_RPT",
    fileName: "Bid_To_Current_SCAC",
  },
  {
    key: "BID_TO_CRNT_LN_SCAC",
    label: "Bid to Current - Lane/SCAC",
    code: "BID_TO_CRNT_LN_SCAC",
    category: "BID_RPT",
    fileName: "Bid_To_Current_Lane_SCAC",
  },
];

const LANE_REPORTS = [
  {
    key: "OVR_AWR_LN",
    label: "Over Awarded Lanes",
    code: "OVR_AWR_LN",
    category: "LANE_RPT",
    fileName: "Over_Awarded_Lanes",
  },
  {
    key: "UNDR_AWR_LN",
    label: "Under Awarded Lanes",
    code: "UNDR_AWR_LN",
    category: "LANE_RPT",
    fileName: "Under_Awarded_Lanes",
  },
  {
    key: "IN_OUT_RPT",
    label: "In / Out Report",
    code: "IN_OUT_RPT",
    category: "LN_RPT",
    fileName: "In_Out_Report",
  },
  {
    key: "NO_AWR_LN",
    label: "No-Awarded Lane",
    code: "NO_AWR_LN",
    category: "LN_RPT",
    fileName: "No_Awarded_Lane",
  },
  {
    key: "AWD_NO_RPM",
    label: "Awarded/No RPM",
    code: "AWD_NO_RPM",
    category: "LN_RPT",
    fileName: "Awarded_No_RPM",
  },
  {
    key: "AWRD_LD_BY_ORG",
    label: "Awarded Loads by Origin",
    code: "AWRD_LD_BY_ORG",
    category: "LN_RPT",
    fileName: "Awarded_Loads_By_Origin",
  },
  {
    key: "LST_12_MN_VAL",
    label: "Last 12 months value",
    code: "LST_12_MN_VAL",
    category: "LN_RPT",
    fileName: "Last_12_Months_Value",
  },
];

const CARRIER_REPORTS = [
  { key: "CARRIER_LANE_RATE", label: "Carrier Lane Rate" },
  { key: "UNDER_FIVE_AWARD", label: "<5% Awarded on Lane" },
  { key: "LANES_NEED_MILE_UPDATES", label: "Lanes that need mile updates" },
];

export const getCarrierLabel = (opt) => opt?.scacCode || "";
export const getServiceLabel = (opt) => opt?.serviceCode || "";
export const getZoneLabel = (opt) => opt?.zoneCode || "";

function DrmReportFilters() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { blob, isLoading, errorMessage, isFileExist } = useSelector((state) => state.ceaZipReportData);
  const { reportDetails, isLoading: reportLoading, errorMessage: reportError, isDownloading, downloadError, downloadedRows, downloadFileName } = useSelector((state) => state.drmReportDetails);
  const { userInfo } = useSelector((state) => state.user);

  const [downloadState, setDownloadState] = useState({ isDownloadOpen: false, dialogTitle: "", dialogContentText: "", variant: "zip" });
  const [snackBarState, setSnackBarState] = useState({ open: false, severity: "info", autoHideDuration: 3000, message: "", });
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportRequestPayload, setReportRequestPayload] = useState(null);
  const [filters, setFilters] = useState({ carriers: [], services: [], originZone: [], destinationZone: [] });

  useEffect(() => {
    if (!isLoading && isFileExist === "exist") {
      downloadZipFiles(blob);
      setSnackBarState({
        open: true,
        severity: "success",
        autoHideDuration: 3000,
        message: "CEA Profile Download Successful",
      });
    } else if (!isLoading && isFileExist === "" && errorMessage) {
      setSnackBarState({
        open: true,
        severity: "error",
        autoHideDuration: 3000,
        message: errorMessage,
      });
    } else if (!isLoading && isFileExist === "not_exist" && errorMessage) {
      setSnackBarState({
        open: true,
        severity: "info",
        autoHideDuration: 3000,
        message: errorMessage,
      });
    }
  }, [isLoading, blob, errorMessage, isFileExist]);

  const handleDialogClose = useCallback(() => {
    setDownloadState((prev) => ({ ...prev, isDownloadOpen: false }));
    setSelectedReport(null);
    setReportRequestPayload(null);
    dispatch(resetDrmReportDetails());
  }, []);

  const handleConfirm = useCallback(() => {
    if (downloadState.variant === "report") {
      if (!selectedReport || !reportRequestPayload) {
        setSnackBarState({
          open: true,
          severity: "warning",
          autoHideDuration: 3000,
          message: t("drmt_report_missing_selection"),
        });
        return;
      }

      const totalPages = reportDetails?.totalPages || 0;
      const pageSize = 1000;
      dispatch(
        downloadDrmReportDetails({
          reqBody: reportRequestPayload,
          reportCode: selectedReport.code,
          totalPages,
          pageSize,
          fileName: selectedReport.fileName,
        })
      );
    } else {
      dispatch(getDrmZipReportData());
      handleDialogClose();
    }
  }, [
    downloadState.variant,
    handleDialogClose,
    reportDetails?.pageSize,
    reportDetails?.totalPages,
    reportRequestPayload,
    selectedReport,
  ]);

  const buildRequestPayload = useCallback(
    (reportConfig) => {
      if (!reportConfig) {
        return null;
      }

      return {
        reportCode: reportConfig.code,
        reportCategory: reportConfig.category,
        userCode: userInfo?.userName || "",
        carriers: filters.carriers,
        services: filters.services,
        originZone: filters.originZone,
        destinationZone: filters.destinationZone,
      };
    },
    [filters, userInfo]
  );

  const handleDialogOpen = useCallback(
    (report) => {
      if (report?.variant === "zip") {
        setSelectedReport(null);
        setReportRequestPayload(null);
        dispatch(resetDrmReportDetails());
        setDownloadState({
          isDownloadOpen: true,
          dialogTitle: `${t("drmt_report_download")} ${report.label}`,
          dialogContentText: t("drmt_report_download_confirmation"),
          variant: "zip",
        });
        return;
      }

      if (report?.code && report?.category) {
        const payload = buildRequestPayload(report);

        if (!payload) {
          return;
        }

        setSelectedReport(report);
        setReportRequestPayload(payload);
        dispatch(resetDrmReportDetails());
        dispatch(
          getDrmReportDetails({
            pageNum: 1,
            pageSize: 1000,
            reqBody: payload,
          })
        );
        setDownloadState({
          isDownloadOpen: true,
          dialogTitle: `${t("drmt_report_download")} ${report.label}`,
          dialogContentText: t("drmt_report_download_confirmation"),
          variant: "report",
        });
        return;
      }

      setSnackBarState({
        open: true,
        severity: "info",
        autoHideDuration: 3000,
        message: t("drmt_report_not_configured"),
      });
    },
    [buildRequestPayload]
  );

  const handleClose = () => {
    setSnackBarState({
      open: false,
      severity: "info",
      autoHideDuration: 3000,
      message: "",
    });
  };
  const updateFilter = useCallback((key, value) => {
    const normalized = Array.isArray(value) ? value.filter(Boolean) : value ? [value] : [];
    setFilters((prev) => ({ ...prev, [key]: normalized }));
  }, []);

  const handleFilterSet = useCallback(
    (key) => (value) => {
      updateFilter(key, value);
    },
    [updateFilter]
  );

  useEffect(() => {
    if (reportError) {
      setSnackBarState({
        open: true,
        severity: "error",
        autoHideDuration: 4000,
        message: reportError,
      });
    }
  }, [reportError]);

  useEffect(() => {
    if (downloadError) {
      setSnackBarState({
        open: true,
        severity: "error",
        autoHideDuration: 4000,
        message: downloadError,
      });
    }
  }, [downloadError]);

  useEffect(() => {
    if (
      downloadState.variant === "report" &&
      !isDownloading &&
      downloadFileName
    ) {
      if (Array.isArray(downloadedRows) && downloadedRows.length > 0) {
        exportToExcel(downloadedRows, downloadFileName);
        setSnackBarState({
          open: true,
          severity: "success",
          autoHideDuration: 3000,
          message: t("drmt_report_download_success"),
        });
      } else {
        setSnackBarState({
          open: true,
          severity: "info",
          autoHideDuration: 3000,
          message: t("drmt_report_no_data"),
        });
      }
      handleDialogClose();
    }
  }, [ downloadFileName, downloadState.variant, downloadedRows, handleDialogClose, isDownloading ]);
  
  return (
    <Stack sx={{ width: "100%", p: 0 }} spacing={3}>
      <Card>
        <CardContent>
          <Stack spacing={2} direction="row" alignItems="center">
            <Grid container spacing={2}>
              <BaseMainFilterAutocomplete
                label="Division"
                value={mock_divisions?.data[6]}
                multiple={true}
                options={mock_divisions.data}
                accessor="divisionCode"
                disabled={true}
                fullWidth
                size="small"
                gridProps={{ xs: 2.4, sm: 2.4, md: 2.4 }}
              />
              <Grid item xs={2.4}>
                {createLiveLoadAutocompleteColumnFilter({
                  key: "carriers",
                  getLabel: getCarrierLabel,
                })({
                  value: filters.carriers[0] || "",
                  header: "carrierScac",
                  handleFilterSet: handleFilterSet("carriers"),
                  multiple: false,
                })}
              </Grid>
              <Grid item xs={2.4}>
                {createLiveLoadAutocompleteColumnFilter({
                  key: "services",
                  getLabel: getServiceLabel,
                })({
                  value: filters.services[0] || "",
                  header: "serviceScac",
                  handleFilterSet: handleFilterSet("services"),
                  multiple: false,
                })}
              </Grid>
              <Grid item xs={2.4}>
                {createLiveLoadAutocompleteColumnFilter({
                  key: "zones",
                  getLabel: getZoneLabel,
                })({
                  value: filters.originZone[0] || "",
                  header: "originZone",
                  handleFilterSet: handleFilterSet("originZone"),
                  multiple: false,
                })}
              </Grid>
              <Grid item xs={2.4}>
                {createLiveLoadAutocompleteColumnFilter({
                  key: "zones",
                  getLabel: getZoneLabel,
                })({
                  value: filters.destinationZone[0] || "",
                  header: "destinationZone",
                  handleFilterSet: handleFilterSet("destinationZone"),
                  multiple: false,
                })}
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Stack spacing={3} sx={{ width: "100%" }}>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ textAlign: "left", mb: 1 }}
          >
            Bid Reports
          </Typography>
          <Grid container spacing={3} sx={{ width: "100%" }}>
            {BID_REPORTS.map((report, index) => (
              <Grid item xs={12} sm={4} key={report.key} sx={{ display: "flex" }}>
                <CustomButton
                  className="dark-blue-btn-report"
                  datatestid={`bid_report_${index}`}
                  onClick={() => handleDialogOpen(report)}
                  size="small"
                  btnType="nonary"
                  fullWidth
                >
                  {report.label}
                </CustomButton>
              </Grid>
            ))}
          </Grid>
        </Stack>

        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ textAlign: "left", mb: 1 }}
          >
            Lane Reports
          </Typography>
          <Grid container spacing={3} sx={{ width: "100%" }}>
            {LANE_REPORTS.map((report, index) => (
              <Grid item xs={12} sm={4} key={report.key} sx={{ display: "flex" }}>
                <CustomButton
                  className="dark-blue-btn"
                  datatestid={`lane_report_${index}`}
                  onClick={() => handleDialogOpen(report)}
                  size="small"
                  btnType="nonary"
                  fullWidth
                >
                  {report.label}
                </CustomButton>
              </Grid>
            ))}
          </Grid>
        </Stack>

        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ textAlign: "left", mb: 1 }}
          >
            Carrier Reports
          </Typography>
          <Grid container spacing={3} sx={{ width: "100%" }}>
            {CARRIER_REPORTS.map((report, index) => (
              <Grid item xs={12} sm={4} key={report.key} sx={{ display: "flex" }}>
                <CustomButton
                  className="dark-blue-btn"
                  datatestid={`carrier_report_${index}`}
                  onClick={() => handleDialogOpen(report)}
                  size="small"
                  btnType="nonary"
                  fullWidth
                >
                  {report.label}
                </CustomButton>
              </Grid>
            ))}
          </Grid>
        </Stack>

        <DialogConformationMsg
          open={downloadState.isDownloadOpen}
          handleClose={handleDialogClose}
          dialogTitle={downloadState.dialogTitle}
          dialogContentText={downloadState.dialogContentText}
          handleConfirm={handleConfirm}
          cancel="Cancel"
          confirm="Download"
          startIcon={<FileDownloadIcon />}
          confirmDisabled={
            downloadState.variant === "report" &&
            (reportLoading || !selectedReport || !reportRequestPayload)
          }
          confirmLoading={
            downloadState.variant === "report" ? isDownloading : false
          }
        />
        <CustomSnackbar
          open={snackBarState.open}
          severity={snackBarState.severity}
          autoHideDuration={snackBarState.autoHideDuration}
          datatestId={snackBarState.datatestId}
          message={snackBarState.message}
          handleClose={handleClose}
        />
      </Stack>
    </Stack>
  );
}

export default DrmReportFilters;
